import { useState, useRef, useEffect } from "react";
import { MessageCircle, Send, X, HelpCircle, ChevronRight, Loader2 } from "lucide-react";

type Message = {
  id: string; role: string; message: string;
  userName: string; adminName: string | null; createdAt: string;
};

type LeadInfo = {
  name: string; company: string; email: string; country: string; phone: string;
};

// [code, name, dialCode, phoneExample, formatHint]
const COUNTRIES: [string, string, string, string, string][] = [
  ["AF","Afghanistan","+93","70 123 4567","7-8 digits after +93"],
  ["AL","Albania","+355","67 123 4567","9 digits after +355"],
  ["DZ","Algeria","+213","551 23 45 67","9 digits after +213"],
  ["AO","Angola","+244","923 123 456","9 digits after +244"],
  ["AR","Argentina","+54","11 1234 5678","10 digits after +54"],
  ["AU","Australia","+61","4XX XXX XXX","9 digits after +61"],
  ["AT","Austria","+43","664 123 456","7-10 digits after +43"],
  ["BH","Bahrain","+973","3600 0000","8 digits after +973"],
  ["BD","Bangladesh","+880","1711 123 456","10 digits after +880"],
  ["BE","Belgium","+32","470 12 34 56","9 digits after +32"],
  ["BR","Brazil","+55","11 91234 5678","11 digits after +55"],
  ["CA","Canada","+1","416 123 4567","10 digits after +1"],
  ["CL","Chile","+56","9 1234 5678","9 digits after +56"],
  ["CN","China","+86","138 0013 8000","11 digits after +86"],
  ["CO","Colombia","+57","300 123 4567","10 digits after +57"],
  ["HR","Croatia","+385","91 234 5678","8-9 digits after +385"],
  ["CY","Cyprus","+357","96 123 456","8 digits after +357"],
  ["CZ","Czech Republic","+420","601 123 456","9 digits after +420"],
  ["DK","Denmark","+45","20 12 34 56","8 digits after +45"],
  ["EG","Egypt","+20","100 123 4567","10 digits after +20"],
  ["ET","Ethiopia","+251","91 123 4567","9 digits after +251"],
  ["FI","Finland","+358","40 123 4567","9-10 digits after +358"],
  ["FR","France","+33","6 12 34 56 78","9 digits after +33"],
  ["DE","Germany","+49","151 1234 5678","10-11 digits after +49"],
  ["GH","Ghana","+233","24 123 4567","9 digits after +233"],
  ["GR","Greece","+30","691 234 5678","10 digits after +30"],
  ["HK","Hong Kong","+852","9123 4567","8 digits after +852"],
  ["HU","Hungary","+36","30 123 4567","9 digits after +36"],
  ["IN","India","+91","98765 43210","10 digits after +91"],
  ["ID","Indonesia","+62","812 3456 7890","9-12 digits after +62"],
  ["IQ","Iraq","+964","770 123 4567","10 digits after +964"],
  ["IE","Ireland","+353","85 123 4567","9 digits after +353"],
  ["IL","Israel","+972","50 123 4567","9 digits after +972"],
  ["IT","Italy","+39","320 123 4567","9-10 digits after +39"],
  ["JP","Japan","+81","90 1234 5678","10-11 digits after +81"],
  ["JO","Jordan","+962","79 123 4567","9 digits after +962"],
  ["KE","Kenya","+254","712 345 678","9 digits after +254"],
  ["KW","Kuwait","+965","9000 0000","8 digits after +965"],
  ["LB","Lebanon","+961","3 123 456","7-8 digits after +961"],
  ["LY","Libya","+218","91 123 4567","9 digits after +218"],
  ["MY","Malaysia","+60","12 345 6789","9-10 digits after +60"],
  ["MX","Mexico","+52","1 55 1234 5678","10 digits after +52"],
  ["MA","Morocco","+212","612 345 678","9 digits after +212"],
  ["NL","Netherlands","+31","6 12345678","9 digits after +31"],
  ["NZ","New Zealand","+64","21 123 4567","8-9 digits after +64"],
  ["NG","Nigeria","+234","801 234 5678","10 digits after +234"],
  ["NO","Norway","+47","412 34 567","8 digits after +47"],
  ["OM","Oman","+968","9123 4567","8 digits after +968"],
  ["PK","Pakistan","+92","300 1234567","10 digits after +92"],
  ["PA","Panama","+507","6000 0000","8 digits after +507"],
  ["PE","Peru","+51","987 654 321","9 digits after +51"],
  ["PH","Philippines","+63","917 123 4567","10 digits after +63"],
  ["PL","Poland","+48","501 234 567","9 digits after +48"],
  ["PT","Portugal","+351","912 345 678","9 digits after +351"],
  ["QA","Qatar","+974","5000 0000","8 digits after +974"],
  ["RO","Romania","+40","712 345 678","9 digits after +40"],
  ["RU","Russia","+7","912 345 6789","10 digits after +7"],
  ["SA","Saudi Arabia","+966","5X XXX XXXX","9 digits after +966"],
  ["SG","Singapore","+65","9123 4567","8 digits after +65"],
  ["ZA","South Africa","+27","71 234 5678","9 digits after +27"],
  ["KR","South Korea","+82","10 1234 5678","9-10 digits after +82"],
  ["ES","Spain","+34","612 345 678","9 digits after +34"],
  ["SE","Sweden","+46","70 123 45 67","9 digits after +46"],
  ["CH","Switzerland","+41","78 123 45 67","9 digits after +41"],
  ["TW","Taiwan","+886","912 345 678","9 digits after +886"],
  ["TH","Thailand","+66","81 234 5678","9 digits after +66"],
  ["TN","Tunisia","+216","20 123 456","8 digits after +216"],
  ["TR","Turkey","+90","532 123 4567","10 digits after +90"],
  ["AE","United Arab Emirates","+971","50 123 4567","9 digits after +971"],
  ["GB","United Kingdom","+44","7700 900000","10 digits after +44"],
  ["US","United States","+1","212 555 0100","10 digits after +1"],
  ["VN","Vietnam","+84","90 123 4567","9-10 digits after +84"],
  ["YE","Yemen","+967","711 234 567","9 digits after +967"],
];

const COUNTRY_MAP = Object.fromEntries(
  COUNTRIES.map(([code, , dialCode, example, hint]) => [code, { dialCode, example, hint }])
);

const flagEmoji = (code: string) =>
  String.fromCodePoint(0x1F1E6 + code.charCodeAt(0) - 65) +
  String.fromCodePoint(0x1F1E6 + code.charCodeAt(1) - 65);

const HELP_TOPICS: Record<string, { title: string; tips: string[] }> = {
  "/": {
    title: "Welcome to FinProject",
    tips: [
      "Explore our subscription plans to find the right fit",
      "Start a free demo to test all features instantly",
      "Ask us anything — we're here to help you get started",
    ],
  },
  "/app": {
    title: "Getting Started",
    tips: [
      "Navigate between projects using the sidebar",
      "Create your first project from the dashboard",
      "Set up clients and subcontractors before invoicing",
    ],
  },
  invoices: {
    title: "Invoicing Help",
    tips: [
      "Create invoices from the Invoices tab",
      "Track payment status and send reminders",
      "Export invoices as PDF for your clients",
    ],
  },
  budget: {
    title: "Budget Management",
    tips: [
      "Set up cost categories and budget lines",
      "Track actual vs budgeted amounts",
      "Monitor variances in real-time",
    ],
  },
};

function getContextHelp(path: string) {
  if (path === "/") return HELP_TOPICS["/"];
  for (const [key, help] of Object.entries(HELP_TOPICS)) {
    if (key !== "/" && path.includes(key)) return help;
  }
  return HELP_TOPICS["/app"];
}

const LEAD_KEY = "fp_chat_lead";

export default function ChatWidget() {
  const isWebsite = window.location.pathname === "/";

  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<"lead" | "chat" | "help">("chat");
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(() =>
    localStorage.getItem("fp_chat_conv")
  );
  const [guestId, setGuestId] = useState<string | null>(() =>
    localStorage.getItem("fp_chat_guest")
  );
  const [leadInfo, setLeadInfo] = useState<LeadInfo | null>(() => {
    try { return JSON.parse(localStorage.getItem(LEAD_KEY) || "null"); } catch { return null; }
  });
  const [leadForm, setLeadForm] = useState<LeadInfo>({
    name: "", company: "", email: "", country: "", phone: "",
  });
  const [leadErrors, setLeadErrors] = useState<Partial<LeadInfo>>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    if (isOpen) {
      if (isWebsite && !leadInfo && !conversationId) {
        setMode("lead");
      } else {
        setMode("chat");
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !conversationId || mode !== "chat") return;
    const fetch_ = async () => {
      try {
        let url = `/api/chat/messages?conversationId=${conversationId}`;
        if (guestId) url += `&guestId=${guestId}`;
        const res = await fetch(url, { credentials: "include" });
        if (res.ok) setMessages(await res.json());
      } catch {}
    };
    fetch_();
    pollRef.current = setInterval(fetch_, 4000);
    return () => clearInterval(pollRef.current);
  }, [isOpen, conversationId, guestId, mode]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const validateLead = () => {
    const errs: Partial<LeadInfo> = {};
    if (!leadForm.name.trim()) errs.name = "Required";
    if (!leadForm.company.trim()) errs.company = "Required";
    if (!leadForm.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(leadForm.email))
      errs.email = "Valid email required";
    if (!leadForm.country) errs.country = "Required";
    if (!leadForm.phone.trim()) errs.phone = "Required";
    setLeadErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submitLead = async () => {
    if (!validateLead()) return;
    const dialCode = COUNTRY_MAP[leadForm.country]?.dialCode ?? "";
    const info = {
      ...leadForm,
      phone: leadForm.phone ? `${dialCode} ${leadForm.phone}`.trim() : dialCode,
    };
    setLeadInfo(info);
    localStorage.setItem(LEAD_KEY, JSON.stringify(info));

    try {
      const res = await fetch(`/api/chat/lookup?email=${encodeURIComponent(info.email)}`);
      if (res.ok) {
        const prior = await res.json();
        if (prior?.conversationId) {
          setConversationId(prior.conversationId);
          localStorage.setItem("fp_chat_conv", prior.conversationId);
          if (prior.guestId) {
            setGuestId(prior.guestId);
            localStorage.setItem("fp_chat_guest", prior.guestId);
          }
        }
      }
    } catch {}

    setMode("chat");
  };

  const getLeadPayload = () => {
    if (!isWebsite) return {};
    if (!leadInfo) return {};
    return {
      guestId,
      guestName: leadInfo.name,
      guestEmail: leadInfo.email,
      guestCompany: leadInfo.company,
      guestCountry: leadInfo.country,
      guestPhone: leadInfo.phone,
    };
  };

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      const res = await fetch("/api/chat/send", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: input.trim(),
          page: window.location.pathname,
          conversationId,
          ...getLeadPayload(),
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (!conversationId) {
          setConversationId(data.conversationId);
          localStorage.setItem("fp_chat_conv", data.conversationId);
        }
        if (data.guestId && !guestId) {
          setGuestId(data.guestId);
          localStorage.setItem("fp_chat_guest", data.guestId);
        }
        setInput("");
        let url = `/api/chat/messages?conversationId=${data.conversationId}`;
        const gId = data.guestId || guestId;
        if (gId) url += `&guestId=${gId}`;
        const msgRes = await fetch(url, { credentials: "include" });
        if (msgRes.ok) setMessages(await msgRes.json());
      }
    } finally {
      setSending(false);
    }
  };

  const contextHelp = getContextHelp(window.location.pathname);

  return (
    <>
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-xl shadow-blue-500/30 flex items-center justify-center transition-all hover:scale-105 z-50"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {isOpen && (
        <div
          className="fixed bottom-6 right-6 w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col z-50"
          style={{ height: mode === "lead" ? "auto" : "500px" }}
        >
          <div className="bg-blue-600 text-white rounded-t-2xl px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                <MessageCircle className="w-4 h-4" />
              </div>
              <div>
                <div className="font-semibold text-sm">FinProject Support</div>
                <div className="text-xs text-blue-200">We usually reply within minutes</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {mode !== "lead" && (
                <button
                  onClick={() => setMode(mode === "chat" ? "help" : "chat")}
                  className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                >
                  <HelpCircle className="w-4 h-4" />
                </button>
              )}
              <button onClick={() => setIsOpen(false)} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {mode === "lead" && (
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-600 font-medium">Tell us a bit about yourself to start chatting</p>

              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Full Name *</label>
                <input
                  type="text"
                  value={leadForm.name}
                  onChange={e => setLeadForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="John Smith"
                  className={`w-full px-3 py-2 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${leadErrors.name ? "border-red-400" : "border-gray-200"}`}
                />
                {leadErrors.name && <p className="text-xs text-red-500 mt-0.5">{leadErrors.name}</p>}
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Company Name *</label>
                <input
                  type="text"
                  value={leadForm.company}
                  onChange={e => setLeadForm(f => ({ ...f, company: e.target.value }))}
                  placeholder="Acme Construction LLC"
                  className={`w-full px-3 py-2 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${leadErrors.company ? "border-red-400" : "border-gray-200"}`}
                />
                {leadErrors.company && <p className="text-xs text-red-500 mt-0.5">{leadErrors.company}</p>}
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Email *</label>
                <input
                  type="email"
                  value={leadForm.email}
                  onChange={e => setLeadForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="john@acme.com"
                  className={`w-full px-3 py-2 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${leadErrors.email ? "border-red-400" : "border-gray-200"}`}
                />
                {leadErrors.email && <p className="text-xs text-red-500 mt-0.5">{leadErrors.email}</p>}
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Country *</label>
                <select
                  value={leadForm.country}
                  onChange={e => {
                    setLeadForm(f => ({ ...f, country: e.target.value, phone: "" }));
                    setLeadErrors(prev => ({ ...prev, country: undefined }));
                  }}
                  className={`w-full px-3 py-2 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${leadErrors.country ? "border-red-400" : "border-gray-200"}`}
                >
                  <option value="">Select country…</option>
                  {COUNTRIES.map(([code, name]) => (
                    <option key={code} value={code}>{flagEmoji(code)} {name}</option>
                  ))}
                </select>
                {leadErrors.country && <p className="text-xs text-red-500 mt-0.5">{leadErrors.country}</p>}
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Phone *</label>
                <div className={`flex rounded-xl border overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 ${leadErrors.phone ? "border-red-400" : "border-gray-200"}`}>
                  <span className="flex items-center px-3 bg-slate-50 border-r border-gray-200 text-sm text-slate-600 shrink-0 font-mono whitespace-nowrap">
                    {leadForm.country
                      ? `${flagEmoji(leadForm.country)} ${COUNTRY_MAP[leadForm.country]?.dialCode ?? ""}`
                      : "🌐"}
                  </span>
                  <input
                    type="tel"
                    value={leadForm.phone}
                    onChange={e => setLeadForm(f => ({ ...f, phone: e.target.value.replace(/[^0-9\s\-]/g, "") }))}
                    placeholder={
                      leadForm.country && COUNTRY_MAP[leadForm.country]
                        ? COUNTRY_MAP[leadForm.country].example
                        : "Select country first"
                    }
                    className="flex-1 min-w-0 px-3 py-2 text-sm focus:outline-none bg-white"
                  />
                </div>
                {leadForm.country && COUNTRY_MAP[leadForm.country] && (
                  <p className="text-xs text-blue-500 mt-0.5">
                    Format: {COUNTRY_MAP[leadForm.country].hint}
                  </p>
                )}
                {leadErrors.phone && <p className="text-xs text-red-500 mt-0.5">{leadErrors.phone}</p>}
              </div>

              <button
                onClick={submitLead}
                className="w-full py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors mt-1"
              >
                Start Chat
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {mode === "help" && (
            <div className="flex-1 overflow-y-auto p-5">
              <h3 className="font-semibold text-gray-900 mb-3">{contextHelp?.title}</h3>
              <div className="space-y-2">
                {contextHelp?.tips.map((tip, i) => (
                  <div key={i} className="flex items-start gap-2.5 p-3 bg-blue-50 rounded-xl">
                    <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold shrink-0 mt-0.5">
                      {i + 1}
                    </div>
                    <span className="text-sm text-gray-700">{tip}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setMode("chat")}
                className="mt-4 w-full py-2.5 rounded-xl border border-blue-200 text-blue-600 text-sm font-medium hover:bg-blue-50 transition-colors"
              >
                Still need help? Chat with us
              </button>
            </div>
          )}

          {mode === "chat" && (
            <>
              {isWebsite && leadInfo && (
                <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
                  <span className="text-xs text-blue-700">
                    Chatting as <strong>{leadInfo.name}</strong> · {leadInfo.company}
                  </span>
                  <button
                    onClick={() => {
                      localStorage.removeItem(LEAD_KEY);
                      localStorage.removeItem("fp_chat_conv");
                      localStorage.removeItem("fp_chat_guest");
                      setLeadInfo(null);
                      setConversationId(null);
                      setGuestId(null);
                      setMessages([]);
                      setLeadForm({ name: "", company: "", email: "", country: "", phone: "" });
                      setMode("lead");
                    }}
                    className="text-xs text-blue-500 hover:text-blue-700 underline"
                  >
                    Change
                  </button>
                </div>
              )}

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.length === 0 && (
                  <div className="text-center py-8">
                    <MessageCircle className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm text-gray-500">Send a message to start a conversation</p>
                    <p className="text-xs text-gray-400 mt-1">Our team is here to help</p>
                  </div>
                )}
                {messages.map(msg => (
                  <div key={msg.id} className={`flex ${msg.role === "admin" ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 ${
                      msg.role === "admin" ? "bg-gray-100 text-gray-900" : "bg-blue-600 text-white"
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      <div className={`text-xs mt-1 ${msg.role === "admin" ? "text-gray-400" : "text-blue-200"}`}>
                        {new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              <div className="p-3 border-t border-gray-200">
                <form
                  onSubmit={e => { e.preventDefault(); sendMessage(); }}
                  className="flex gap-2"
                >
                  <input
                    type="text"
                    placeholder="Type a message..."
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    className="flex-1 px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    type="submit"
                    disabled={!input.trim() || sending}
                    className="w-10 h-10 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white flex items-center justify-center transition-colors"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </form>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
